import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { mdiBullhornOutline, mdiLoading, mdiSend } from '@mdi/js'
import { $api } from '../../api/queryClient'
import { apiClient } from '../../api/client'
import type { components } from '../../api/schema'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { invalidateIssueQueries } from './issueFamily'

type Preparation = components['schemas']['IssueNoticePreparation']
type Notice = components['schemas']['IssueNotice']

/**
 * "Prévenir les occupants" (spec 2026-10-27-19 § 5): the audience of a scope,
 * each contact with why they are in it, a draft rendered from the ticket, and
 * one e-mail per ticked recipient. Contacts without an e-mail are listed and
 * counted, never sent to.
 */
export function NoticeDialog({ orgCode, issueUid, open, onOpenChange, onSent }: {
    orgCode: string
    issueUid: string
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Called once the notice is recorded (a notify_occupants step then closes). */
    onSent?: () => void
}) {
    const { t } = useTranslation('issues')
    const queryClient = useQueryClient()
    const [scope, setScope] = useState<string | undefined>(undefined)
    const [prep, setPrep] = useState<Preparation | null>(null)
    const [picked, setPicked] = useState<Set<string>>(new Set())
    const [subject, setSubject] = useState('')
    const [body, setBody] = useState('')
    const [loading, setLoading] = useState(false)
    const [sending, setSending] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!open) {
            setScope(undefined)
            setPrep(null)
            return
        }
        let cancelled = false
        setLoading(true)
        setError(null)
        apiClient.POST('/api/v1/organizations/{orgCode}/issues/{issueUid}/notices/prepare', {
            params: { path: { orgCode, issueUid } },
            body: scope ? { scope_asset_uid: scope } : {},
        }).then(({ data }) => {
            if (cancelled || !data) return
            setPrep(data)
            setPicked(new Set(data.audience.filter((m) => m.reachable).map((m) => m.contact_uid)))
            setSubject((prev) => prev || data.subject)
            setBody((prev) => prev || data.body)
        }).finally(() => { if (!cancelled) setLoading(false) })
        return () => { cancelled = true }
    }, [open, scope, orgCode, issueUid])

    useEffect(() => {
        if (!open) {
            setSubject('')
            setBody('')
        }
    }, [open])

    const toggle = (uid: string, on: boolean) => setPicked((prev) => {
        const next = new Set(prev)
        if (on) next.add(uid)
        else next.delete(uid)
        return next
    })

    const send = async () => {
        if (!prep) return
        setSending(true)
        setError(null)
        const { error: err } = await apiClient.POST('/api/v1/organizations/{orgCode}/issues/{issueUid}/notices', {
            params: { path: { orgCode, issueUid } },
            body: { scope_asset_uid: prep.scope.uid, recipients: [...picked], subject, body },
        })
        setSending(false)
        if (err) {
            setError(t('notice.error'))
            return
        }
        queryClient.invalidateQueries({
            predicate: (q) => (q.queryKey as unknown[])[1] === '/api/v1/organizations/{orgCode}/issues/{issueUid}/notices',
        })
        invalidateIssueQueries(queryClient)
        onSent?.()
        onOpenChange(false)
    }

    const canSend = !sending && picked.size > 0 && subject.trim() !== '' && body.trim() !== ''

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-xl" data-testid="notice-dialog">
                <DialogHeader>
                    <DialogTitle>{t('notice.title')}</DialogTitle>
                    <DialogDescription>{t('notice.description')}</DialogDescription>
                </DialogHeader>
                {!prep ? (
                    <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin" />
                ) : (
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-muted-foreground">{t('notice.scope')}</label>
                            <Select value={prep.scope.uid} onValueChange={(v) => setScope(v)}>
                                <SelectTrigger data-testid="notice-scope-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {prep.scopes.map((s) => (
                                        <SelectItem key={s.uid} value={s.uid} data-testid={`notice-scope-${s.uid}`}>{s.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span data-testid="notice-audience-count">{t('notice.audience', { count: picked.size })}</span>
                            {prep.unreachable > 0 && (
                                <span data-testid="notice-unreachable-count">· {t('notice.unreachable', { count: prep.unreachable })}</span>
                            )}
                            {loading && <AppIcon path={mdiLoading} size="xs" className="motion-safe:animate-spin" />}
                        </div>
                        {prep.audience.length === 0 ? (
                            <p className="text-sm text-muted-foreground" data-testid="notice-audience-empty">{t('notice.empty')}</p>
                        ) : (
                            <ul className="max-h-56 overflow-y-auto divide-y rounded-md border" data-testid="notice-audience">
                                {prep.audience.map((m) => (
                                    <li key={m.contact_uid} className="flex items-center gap-2 px-3 py-1.5 text-sm" data-testid={`notice-audience-row-${m.contact_uid}`}>
                                        <Checkbox
                                            checked={picked.has(m.contact_uid)}
                                            disabled={!m.reachable}
                                            onCheckedChange={(on) => toggle(m.contact_uid, on === true)}
                                            aria-label={m.name}
                                            data-testid={`notice-audience-check-${m.contact_uid}`}
                                        />
                                        <span className="flex-1 min-w-0 truncate">
                                            {m.name}
                                            <span className="text-muted-foreground ml-1.5">{m.email || t('notice.no_email')}</span>
                                        </span>
                                        {m.reasons.map((r) => (
                                            <Badge key={r} variant="outline" className="text-[11px]">{t(`notice.reason.${r}`)}</Badge>
                                        ))}
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-muted-foreground">{t('notice.subject')}</label>
                            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="notice-subject" />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-muted-foreground">{t('notice.body')}</label>
                            <Textarea value={body} onChange={(e) => setBody(e.target.value)} className="min-h-[140px]" data-testid="notice-body" />
                        </div>
                        {error && <p className="text-sm text-destructive" data-testid="notice-error">{error}</p>}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending} data-testid="notice-cancel">{t('notice.cancel')}</Button>
                    <Button onClick={send} disabled={!canSend} data-testid="notice-send">
                        <AppIcon path={sending ? mdiLoading : mdiSend} size="sm" className={`mr-1 ${sending ? 'motion-safe:animate-spin' : ''}`} />
                        {t('notice.send')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

/** The notices sent from a ticket, and the button that sends one. */
export function IssueNoticesCard({ orgCode, issueUid }: { orgCode: string; issueUid: string }) {
    const { t, i18n } = useTranslation('issues')
    const [open, setOpen] = useState(false)
    const notices = $api.useQuery('get', '/api/v1/organizations/{orgCode}/issues/{issueUid}/notices', {
        params: { path: { orgCode, issueUid } },
    })
    const list = (notices.data?.data ?? []) as Notice[]

    return (
        <Card data-testid="issue-notices-card">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <AppIcon path={mdiBullhornOutline} size="sm" />
                    {t('notice.card_title')}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {list.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('notice.none')}</p>
                ) : (
                    <ul className="space-y-1.5" data-testid="issue-notices-list">
                        {list.map((n) => (
                            <li key={n.uid} className="text-sm" data-testid={`issue-notice-row-${n.uid}`}>
                                <div className="text-foreground">{n.subject}</div>
                                <div className="text-[11px] text-muted-foreground">
                                    {t('notice.sent_summary', {
                                        date: new Date(n.created_at).toLocaleDateString(i18n.language),
                                        scope: n.scope_asset_name,
                                        total: n.total,
                                        sent: n.sent,
                                    })}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
                <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="issue-notify-occupants">
                    <AppIcon path={mdiBullhornOutline} size="xs" className="mr-1" />
                    {t('notice.button')}
                </Button>
            </CardContent>
            <NoticeDialog orgCode={orgCode} issueUid={issueUid} open={open} onOpenChange={setOpen} />
        </Card>
    )
}
